import React, { FC, useEffect, useMemo } from 'react';
import { useAction } from '@reatom/npm-react';
import SayKitWeb from '@whitesharx/starfall-frontend-saykit-web';
import Head from 'next/head';
import {
  useServices,
  t,
  ChallengeMode,
  Routes,
  PuzzleCategory,
  WeeklyRewards,
  TAP_EVENTS,
  VIEW_EVENTS,
  Safe,
  useModal,
} from '@whitesharx/starfall-frontend-core';

import { Challenges, StormLimitModal } from '@whitesharx/starfall-frontend-core/challenges_screen';
import '@whitesharx/starfall-frontend-core/challenges_screen.css';

import UserInfo from '~/src/components/UserInfo';
import InfoHeader from '~/src/components/InfoHeader';
import PageLayoutHeader from '~/src/components/PageLayoutHeader';
import ChessRoyaleBanner from '~/src/components/ChessRoyaleBanner';

import { AppComponentType, AppTypes } from '../_app';
import { useRouter } from 'next/router';
import { bannerContainerStyles } from '~/styles/pages/common.css';
import ChallengesSkeletonPage from '~/src/skeletons/Challenges';
import CommonCustomApp from '~/src/components/CustomApp/CommonCustomApp';
import useStormLimit from '~/src/hooks/useStormLimit';
import VIPModal from '~/src/components/VIPModal';
import { updateRightContainerAction } from '~/src/models/layout';

const ChallengesRealPage: FC = () => {
  const { Adsense, Analytics, useResponsive } = useServices();
  const updateRightContainer = useAction(updateRightContainerAction);
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const {
    timeToNextStorm,
    canStartStorm,
    adAvailable,
    isLoading: stormLimitLoading,
    isOpenStormLimitModal,
    openStormLimitModal,
    closeStormLimitModal,
    handleAdViewed,
  } = useStormLimit();
  const { isOpen: isOpenVIPModal, open: openVIPModal, close: closeVIPModal } = useModal();

  const rightContainer = useMemo(() => {
    if (isDesktop) {
      return (
        <>
          <UserInfo />
          {Adsense.renderBanner({
            placement: 'puzzles_screen_banner_right',
            format: 'rectangle',
            slot: 'bannerRight',
            withSafeMargins: true,
          })}
          <div className={bannerContainerStyles}>
            <ChessRoyaleBanner />
          </div>
        </>
      );
    }
  }, [isDesktop]);

  useEffect(() => {
    updateRightContainer(rightContainer);
  }, [rightContainer]);

  useEffect(() => {
    Analytics.sendEvent(VIEW_EVENTS.challenges);
  }, []);

  const startStorm = (showInterstitial: boolean) => {
    Analytics.sendEvent(TAP_EVENTS.challengesStorm);

    const goToGame = () => {
      router.push(Routes.challengesStorm);
    };

    const disabledInterstitials = SayKitWeb.featureFlags.interstitials === 'none';

    if (showInterstitial && !disabledInterstitials) {
      Adsense.showInterstitial('start-storm', goToGame);
    } else {
      goToGame();
    }
  };

  const startPuzzle = (category?: PuzzleCategory) => {
    Analytics.sendEvent(TAP_EVENTS.challengesStorm);

    router.push({
      pathname: Routes.challengesPuzzles,
      ...(category && { query: { category } }),
    });
  };

  const onPlay = (mode: ChallengeMode, category?: PuzzleCategory) => {
    if (mode === ChallengeMode.PUZZLE) {
      startPuzzle(category);
    }

    if (mode === ChallengeMode.STORM) {
      canStartStorm().then((canPlay) => {
        if (!canPlay) {
          openStormLimitModal();
        } else {
          startStorm(true);
        }
      });
    }
  };

  const onAdViewed = () => {
    handleAdViewed(() => startStorm(false));
  };

  return (
    <>
      <Head>
        <title>{t('browser-title.challenges')}</title>
      </Head>
      <>
        {!isDesktop && <InfoHeader />}
        <PageLayoutHeader title={t('challenges.page-title')} name="challenges" />
        <Challenges onPlay={onPlay} stormLoading={stormLimitLoading} />
        {isOpenStormLimitModal && (
          <StormLimitModal
            isOpen={isOpenStormLimitModal}
            adAvailable={adAvailable}
            onRequestClose={closeStormLimitModal}
            onAdViewed={onAdViewed}
            timeToNextStorm={timeToNextStorm}
            onGetVip={openVIPModal}
          />
        )}
        {isOpenVIPModal && <VIPModal onClose={closeVIPModal} />}
        <WeeklyRewards />
        <Safe />
      </>
    </>
  );
};

const ChallengesPage: FC<AppTypes> & AppComponentType = (props) =>
  props.skeleton ? <ChallengesSkeletonPage /> : <ChallengesRealPage {...props} />;

ChallengesPage.render = (props) => <CommonCustomApp {...props} />;

export default ChallengesPage;
